import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeHideableValue} from '../../client-server-common';
import {
    bymoPageGetInnerNavigatablePageById,
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
    doppeHideableValueGetVisibleValue,
    doppePremiumFeatureIsDisabled,
    DoppePremiumFeaturesAlias
} from '../../client-server-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {asArray, hideableValueCreateHidden, immerProduce} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeRenderingInPageSupport
} from "../../client-server-common/doppe-action-types/doppe-action-type-rendering-in-page-support";
import type {DoppeLinkSpec} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeLinkSpecCreateDefault,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl
} from '../../client-server-common/types/doppe-action-cta-button';
import {DoppeDtoInnerPageType} from '../../client-server-common/types/doppe-dto-inner-page';
import {DoppeViewerPageType} from '../../client-server-common/viewer-pages/doppe-viewer-pages';


export namespace DoppeActionTypeBanner {

    export interface ActionSettings {
        /**
         * Link specification - can be a URL string, a page reference, or an entity reference.
         * Wrapped in DoppeHideableValue to allow hiding the link.
         */
        url: DoppeHideableValue<DoppeLinkSpec>;
        image: WixMediaResource | null;
    }
}

export const doppeActionTypeBanner = doppeActionTypeDefineMetadata<DoppeActionTypeBanner.ActionSettings>({
    id: 'io.bymo.action/banner',
    hasNoGoalMeasuring: false,
    isLinkOpener: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            url: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            image: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia)
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: false,
    hasNoLockInnerCTA: true,
    skipReportingOnGoalAchievedOnActionButtonClick: false,
    lockIsNotSupported: true,
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        url: hideableValueCreateHidden(doppeLinkSpecCreateDefault()),
        image: null,
    }),
    canRenderInPage: DoppeActionTypeRenderingInPageSupport.AsCardOnly,
    hasDisplaySettings: false,
    isPremiumAction: (premiumFeaturesStatus) => {
        return doppePremiumFeatureIsDisabled(premiumFeaturesStatus, DoppePremiumFeaturesAlias.bannerAction)
    },
    resolveUsedMedia: actionSettings => {
        return actionSettings.image ? asArray(actionSettings.image) : [];
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.image) {
                actionSettings.image = wixMediaResourceReplaceExternalImage(actionSettings.image, externalImagesUrlToImageResourceMap);
            }
        })
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.image
    },
    resolveMainLink: (actionSettings, _htmlSiteInfo, _viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        const linkSpec = doppeHideableValueGetVisibleValue(actionSettings.url, null);
        if (linkSpec === null) {
            return null;
        }
        
        // Handle simple URL string
        if (doppeLinkSpecIsUrl(linkSpec)) {
            return linkSpec;
        }
        
        // Handle Page link spec - resolve URL from page info using routes manager
        if (doppeLinkSpecIsPage(linkSpec) && bymoPageProps && viewerPagesRoutesManager) {
            const page = bymoPageGetInnerNavigatablePageById(linkSpec.pageId, bymoPageProps);
            if (page) {
                switch (page.type) {
                    case DoppeDtoInnerPageType.Products:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ProductsList
                        });
                    case DoppeDtoInnerPageType.BlogPosts:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.BlogPostsList
                        });
                    case DoppeDtoInnerPageType.Events:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.EventsList
                        });
                    case DoppeDtoInnerPageType.Services:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ServicesList
                        });
                    case DoppeDtoInnerPageType.Content:
                    default:
                        return viewerPagesRoutesManager.contentPage({slug: page.slug});
                }
            }
        }
        
        // For entity links, return null - navigation will be handled in the viewer
        return null;
    }
})